import mixpanel from 'mixpanel-browser';
import type { MutableRefObject } from 'react';
import type { NavigateFunction } from 'react-router-dom';

import { BuildADashTrackingEvents, FeatureFlag } from '@revelio/core';
import type { CompositionPageScope } from '@revelio/core/global-ai-assistant/composition-page-scope';
import { getGlobalAssistantReportCatalogEntry } from '@revelio/core/global-ai-assistant/reports';
import type {
  CompanyDiscoveryAiOutput,
  Tab,
  TalentDiscoveryAiFilterSearchResponseV2,
} from '@revelio/data-access';
import type { ToastApi } from '@revelio/ui';

import {
  type ActivityFilterFlags,
  isActivityFilterOnForPrimary,
} from '../../shared/unleash-flags/use-activity-filter-flags';
import { PRIMARY_FILTER_BY_AI_ENTITY } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import { trackAIAssistantPrompt } from '../CustomDash/components/AIAssistant/track-ai-assistant-prompt';
import type { PositionedItem } from '../CustomDash/components/PageConfigProvider/types/item';
import type { IReportEntityMap } from '../reports/report-builder/02-entity-configuration/mw-reports/utils';
import {
  getReportBuilderPath,
  ReportBuilderStep,
} from '../reports/utils/report-builder-paths';
import { getPromotedTemplateForReportOfferClick } from '../reports/utils/report-template-promotions';
import type { NarrationCollector } from './ga-narration-collection-context';
import { getDeniedStandardNavigateOptionIds } from './global-ai-assistant-access';
import {
  type CustomDashAssistantEntityType,
  type GlobalAssistantContextPolicy,
  type GlobalAssistantDiscoveryKind,
  type GlobalAssistantEntityType,
  type GlobalAssistantReportOffer,
  type GlobalAssistantSuggestMatch,
  toCustomDashAssistantEntityType,
  toScreenerAssistantEntityType,
} from './global-ai-assistant-api';
import {
  COMMAND_TTL_MS,
  changesPageScope,
  channelEventsSince,
  type GlobalAiCommand,
  useGlobalAiCommandStore,
  waitForCommandComplete,
  waitForTaxonomyChange,
} from './global-ai-assistant-command-store';
import type { GlobalAiCustomDashAssistContext } from './global-ai-assistant-context';
import {
  applyConversationPolicy,
  resolveFollowOnContextPolicy,
  toConversationPayload,
} from './global-ai-assistant-context-policy';
import {
  buildBoundedConversationContext,
  type GlobalAiConversationSummary,
} from './global-ai-assistant-conversation';
import {
  buildGlobalAssistantCurrentPageContext,
  getDiscoveryApplyPrompt,
  isGlobalAssistantCurrentPageFilterable,
} from './global-ai-assistant-discovery-followup';
import {
  armContinuation,
  beginTurn,
  endTurn,
  type GlobalAiEngineApi,
  markTurnStopping,
  type OptionTrigger,
  selectIsTurnRunning,
  useGlobalAiEngineStore,
} from './global-ai-assistant-engine-store';
import { isGlobalAssistantFilterApplyCapablePath } from './global-ai-assistant-filter-apply-capability';
import {
  confirmGlobalAiAssistantRedirect,
  getGlobalAiAssistantRoutePathname,
} from './global-ai-assistant-navigation-guard';
import { getGlobalAssistantPageMatchAction } from './global-ai-assistant-page-match-routing';
import { getGlobalAssistantReportAccess } from './global-ai-assistant-report-access';
import { launchReportFromOffer } from './global-ai-assistant-report-launch';
import {
  type GlobalAssistantResolvedRouteFilters,
  getGlobalAssistantRouteFilterKind,
} from './global-ai-assistant-route-filter-kind';
import { getRouteFilterResponse } from './global-ai-assistant-route-filter-prefetch';
import { resolveNavigateToOptionId } from './global-ai-assistant-standard-page-match';
import {
  createStoppedMessage,
  type GlobalAiTranscriptMessage,
  USER_STOP_ABORT_REASON,
} from './global-ai-assistant-transcript';
import type { NavigateToOptionResult } from './global-ai-client-tool-deps';
import type {
  HeadlessBuildTaxonomy,
  HeadlessGeneratedPage,
} from './global-ai-generate-headless';
import { runGeographyTaxonomySwitch } from './global-ai-geo-taxonomy-switch';
import { formatReportPrefillNarration } from './global-ai-report-prefill-narration';
import {
  globalAssistantPathsMatch,
  inferEntityTypeFromNavigatePath,
} from './global-assistant-route-filter-path';
import type { ReasoningTrace } from './reasoning-trace';
import {
  buildReportOfferLaunchKey,
  type ReportOfferCardFields,
} from './report-offer-launch-key';
import { runHeadlessBuildWithTrace } from './run-headless-build-with-trace';
import { beginSeededBuildNarration } from './seeded-build-narration';
import { buildReportPrefillDigest } from './turn-outcome-digest';
import type { AssistantTaxonomy } from './use-global-ai-assistant-taxonomy';
import type { TurnStepContextFields } from './use-global-ai-turn-runner';

/**
 * Framework-free core for the global AI assistant engine.
 *
 * Extraction target (Phase 6): the render-null engine component's
 * orchestration moves here piece by piece so it can be unit-tested without a
 * React renderer. See docs/superpowers/plans/2026-08-09-ai-assistant-engine-core-extraction.md.
 */

/**
 * THE route accessor for the engine — one convention instead of three.
 *
 * Reads the LIVE browser path at call time, never a render-closure snapshot.
 * This is correct for every engine site class:
 *
 * - Turn-runner deps (`currentPageLabel`, `emitCreateCurrentPage`, …): the
 *   runner captures `deps` once at turn start; when the turn itself navigates
 *   (navigate → search_filters → apply_filters), a closure over the
 *   turn-start location would still report the page we LEFT, so
 *   `apply_filters` would target the old page and the bridge (now on the new
 *   page) would never match the command. Live read = post-navigate
 *   route.
 * - Long-lived async callbacks (the seeded-build landing narration completes
 *   up to ~30s after its creating render): a live read replaces the
 *   `pathnameRef` mirror those callbacks used to need.
 * - Click-time reads (guards, tracking, same-page checks): the data router
 *   completes navigation asynchronously (`navigate()` never mutates
 *   `window.location` in the caller's frame), so within a click handler the
 *   live value IS the render value — converting these sites changes nothing
 *   while retiring the render-closure convention.
 *
 * That last equivalence holds for THIS app specifically, and is worth
 * re-checking if any of it changes: the app runs a data router
 * (`createBrowserRouter`), so `window.location.pathname` tracks the router's
 * pathname 1:1 — no basename is configured (a basename would make the two
 * differ by that prefix), and react-router's only synchronous-navigation
 * shortcut is the hash-only case, which none of the engine's navigate
 * targets are.
 */
export const getCurrentRoute = (): string => window.location.pathname;

const SAME_PAGE_FILTER_APPLY_MESSAGE = 'Applying these filters to this page.';

/**
 * Navigate to the ROUTE part of a target path when it differs from the raw
 * path (a draft path can carry a query string the router must not keep), and
 * report the pathname the caller should treat as the destination.
 */
function normalizeAndNavigateToPath(
  targetPath: string,
  navigate: NavigateFunction,
  skipGuard: boolean
): string {
  const normalizedPath = getGlobalAiAssistantRoutePathname(targetPath);
  if (normalizedPath === targetPath) {
    return normalizedPath;
  }
  navigate(
    normalizedPath,
    skipGuard
      ? { replace: true, state: { skipGuard: true } }
      : { replace: true }
  );
  return normalizedPath;
}

/**
 * How far the filter-apply failure channel has been surfaced into chat, keyed
 * by conversation. MODULE-level, not an instance field, because the watermark
 * belongs to the CONVERSATION, not to an engine instance: the engine unmounts
 * on hidden paths (`GlobalAIAssistantHost` returns null) while the provider —
 * and the transcript it writes to — stays mounted above it, and the shell
 * constructs a NEW instance per mount. An instance-seeded watermark restarts
 * at the channel head on every remount and permanently drops a failure
 * published while the user was away (a `/report/:templateId` bridge pruning a
 * TTL-expired apply, for example).
 *
 * Keying on the tracking id is what keeps that from replaying stale failures:
 * New Chat, hydration and a fresh provider mount all stamp a new id, which
 * fails the key check and re-seeds. The component subscribes to the id, so the
 * re-seed happens AT the id change — never at the moment the new
 * conversation's first failure arrives, which would consume it.
 */
const filterApplyFailureWatermark: { conversationId: string; seq: number } = {
  conversationId: '',
  seq: 0,
};

/** Named like the compatibility toast: a few, then a count for the rest. */
function formatDroppedNames(names: string[]): string {
  const shown = names.slice(0, 3);
  const rest = names.length - shown.length;
  // With a remainder the list stays comma-separated, so the trailing "and N
  // more" is the only "and" ("A, B, C and 2 more", never "B and C and 2 more").
  if (rest > 0) {
    return `${shown.join(', ')} and ${rest} more`;
  }
  if (shown.length <= 1) {
    return shown[0] ?? '';
  }
  return `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
}

export function countUserTurns(
  transcript: GlobalAiTranscriptMessage[]
): number {
  return transcript.reduce((count, message) => {
    return message.role === 'user' ? count + 1 : count;
  }, 0);
}

/**
 * The turn that produced `messageId`, so a click on an older answer joins to
 * that answer's outcome rather than the latest turn. Falls back to the latest
 * turn when the message is gone.
 */
export function turnIndexOfMessage(
  transcript: GlobalAiTranscriptMessage[],
  messageId: string | undefined
): number {
  const index = messageId
    ? transcript.findIndex((m) => m.id === messageId)
    : -1;
  return countUserTurns(
    index < 0 ? transcript : transcript.slice(0, index + 1)
  );
}

// Engine turn-state writes go straight to the store (module-level setState is
// safe from async callbacks that outlive a render, including after unmount).
// Turn lifecycle writes go through the store's turnId-keyed actions
// (beginTurn/markTurnStopping/endTurn), so an OLD engine instance's `finally`
// landing after a remount is structurally inert — see the store docblocks.
const setEngineError = (value: string | null) =>
  useGlobalAiEngineStore.setState({ error: value });
const setReportOfferLaunchingKey = (value: string | null) =>
  useGlobalAiEngineStore.setState({ reportOfferLaunchingKey: value });

/**
 * The clarification the user is answering, if any: the newest assistant
 * clarification NOT already separated from the draft by a user message.
 * Scanning stops at the first user message so an answered clarification can
 * never re-classify a later, unrelated prompt.
 */
function findLatestClarification(
  transcript: GlobalAiTranscriptMessage[]
): Extract<GlobalAiTranscriptMessage, { kind: 'clarification' }> | undefined {
  for (let i = transcript.length - 1; i >= 0; i -= 1) {
    const m = transcript[i];
    if (m && m.role === 'assistant' && m.kind === 'clarification') {
      return m;
    }
    if (m && m.role === 'user') {
      return undefined;
    }
  }
  return undefined;
}

/**
 * Refs created ONCE per assistant provider and stable for the engine's whole
 * life — the constructor takes them; everything render-fresh goes through
 * `updateDeps` instead.
 */
export type AssistantEngineStableRefs = {
  /** Active tool-loop turn controller (see the context docblock). */
  turnAbortRef: MutableRefObject<AbortController | null>;
  /** Page-stream SSE controller, reused by the bridge (kept separate from the turn's). */
  streamAbortRef: MutableRefObject<AbortController | null>;
  /** Custom Dash edit surface hints for global assistant requests (updated by bridge). */
  customDashAssistContextRef: MutableRefObject<GlobalAiCustomDashAssistContext>;
  conversationTrackingIdRef: MutableRefObject<string>;
};

/**
 * Render-fresh dependency set, replaced by the component in the RENDER BODY
 * every render (H1 — never an effect: the published API used to be reassigned
 * during render, and a click landing between render and effects must still see
 * fresh closures).
 */
export type AssistantEngineDeps = {
  /** Injected (not the module fn) so specs can fake routes without touching history. */
  getCurrentRoute: () => string;
  /**
   * Per-entity taxonomy resolver — SAVED PREFS ONLY (see the engine
   * component's docblock at the hook read for the full product rationale).
   * Fresh per render, so a turn that outlives its creating render still
   * resolves under the freshest preference-backed values.
   */
  taxonomyByEntity: (
    entityType: GlobalAssistantEntityType
  ) => AssistantTaxonomy;
  /**
   * Live transcript, read straight off its zustand store. A getter, not a
   * value: turn-runner deps run mid-turn, so they must see messages appended
   * since this dep set was built.
   */
  getTranscript: () => GlobalAiTranscriptMessage[];
  appendTranscript: (message: GlobalAiTranscriptMessage) => void;
  /** Drops every message; the "New chat" reset's transcript half. */
  clearTranscript: () => void;
  /** Streams a live reasoning trace into an already-appended trace message. */
  updateReasoningTrace: (id: string, trace: ReasoningTrace) => void;
  /** Rewrites an assistant bubble's text in place (headless build narration). */
  updateAssistantMessageText: (id: string, text: string) => void;
  setGenerationStatus: (value: string | null) => void;
  /**
   * The status currently on screen. Read by the TTL status-clear timers
   * (route-filter resolve, deterministic apply) so they only wipe a status
   * they themselves set — replaces the `generationStatusRef` mirror, which
   * existed for exactly this "read the latest value from an async callback"
   * need. A per-render closure over the render's value gives the same answer:
   * `updateDeps` runs in every render body, so the newest getter always wins.
   */
  getGenerationStatus: () => string | null;
  /**
   * Whether the taxonomy PREFERENCES have hydrated. Read fresh on every poll
   * iteration of the turn gate's hold (see `waitForTaxonomyReady`), which is
   * exactly what the `isTaxonomyReadyRef` mirror it replaces existed for.
   */
  isTaxonomyReady: boolean;
  /** Last prompt the user actually typed. */
  lastUserPrompt: string | null;
  setLastUserPrompt: (value: string | null) => void;
  conversationSummary: GlobalAiConversationSummary | null;
  sessionEntityScope: GlobalAssistantEntityType | null;
  userTabs: Tab[];
  reportBuilderConfig: IReportEntityMap;
  /**
   * Every Unleash flag + the CustomReports flag on its own — the pair the
   * report-template promotion check takes (a promoted template turns an
   * otherwise-unavailable report type into an allowed launch).
   */
  allFlags: Record<FeatureFlag, boolean>;
  /** App toast — the report prefill's "some filters need manual selection" warning. */
  toast: ToastApi;
  /** React-router navigation — the engine's only way to move the browser. */
  navigate: NavigateFunction;
  /**
   * Create an empty Custom Dash draft (and navigate to it), returning the new
   * draft's path — `usePageManager().createPageDraft`, which accepts the wider
   * `EntityType`; the engine only ever builds assistant-supported entities.
   */
  createPageDraft: (
    entityType: CustomDashAssistantEntityType,
    skipGuard?: boolean
  ) => string | null;
  /**
   * The unsaved-changes prompt behind `confirmGlobalAiAssistantRedirect`:
   * the component's in-app UnsavedChangesDialog — never `window.confirm`,
   * which the browser can suppress after repeated dialogs and then return
   * `false` instantly with no user involvement (the invisible "clicked the
   * build link and nothing happened"). Resolves with the user's choice; the
   * pending resolver is parked on `leaveConfirmSlot` so `dispose()` can
   * settle it as "stay".
   */
  confirmLeave: (message: string) => Promise<boolean>;
  /**
   * The activity filter's per-page gate (`activity-taxonomy-company` on
   * Company pages, always on for every other page) AND Unleash readiness,
   * folded by the component exactly as FilterProvider gates the
   * ACTIVITY filter: an unresolved flag reads `false`, but a bootstrap-cached
   * `true` can land a render BEFORE `flagsReady`, and claiming the capability
   * in that window would have the backend extract activity filters the page
   * cannot hold. The single flag read for the assistant — the turn-step
   * context and every route-filter resolution take their value from here,
   * each picking the half that owns its target entity.
   */
  activityFilterFlags: ActivityFilterFlags;
  /**
   * Same source the chart menu gates its Download items on. Resolves async,
   * so it is `null` until the logged-in user lands — `buildTurnContext` only
   * sends it when definitively `true`, so a still-loading value is never read
   * server-side as a confirmed paid account.
   */
  isTrialUser: boolean | null;
  /**
   * The mounted bridge's page filters, keyed by pathname, as published by
   * `usePublishCurrentPageFilters`. A page-relative read layers these under
   * the ask, so the figure covers the population the user is looking at.
   */
  currentPageFiltersByPath: Record<string, CompositionPageScope>;
  /**
   * The tool-loop runner's `runTurn`. The runner hook only STORES this
   * class's bound methods (it never calls one at render time), so the
   * component calls the hook first and supplies the whole dep set — this
   * included — in ONE render-body `updateDeps`.
   */
  runTurn: (
    prompt: string,
    options?: { suppressReportOffer?: boolean }
  ) => Promise<void>;
};

/**
 * Plain-class core of the assistant engine (no React). One instance per
 * engine mount (`useState(() => new AssistantEngine(...))` — per-mount
 * lifetimes are part of the contract: pending continuations and the
 * instance-owned slots die with the engine, exactly as the component-local
 * refs they replace did).
 *
 * Owns the turn-runner dep cluster (leaf emit/resolve methods), the turn
 * gate, the navigation + route-filter cluster, and the report/build cluster —
 * i.e. everything the render-null engine component used to orchestrate.
 *
 * The instance IS the published `GlobalAiEngineApi`: the component registers
 * it in the engine store on mount and nulls the field again in `dispose()`
 * (the skins `?.`-guard on it, and conversation persistence uses `api !== null`
 * as its "engine mounted" signal — see `dispose`).
 */
export class AssistantEngine implements GlobalAiEngineApi {
  private readonly stable: AssistantEngineStableRefs;

  /**
   * Set by `dispose()`, cleared by `onMount()`. Every PUBLISHED method checks
   * it and silently no-ops when set (dev-only warn) — matching the posture of
   * the module-level store writers, which are safe from async callbacks that
   * outlive a mount. Callers that captured a bound method before the unmount
   * (the continuation hook's `startAssistantTurn`, the seeded-build collector
   * firing up to 30s after its click, an offer handler memoized in the
   * transcript pane) therefore get a quiet no-op instead of the "used after
   * unmount" throw the old per-render delegate object raised — nothing catches
   * that throw, so it only ever surfaced as an unhandled rejection.
   */
  private disposed = false;

  /**
   * Aborted when the engine unmounts (e.g. navigate to a hidden path). The
   * turn gate's taxonomy-hold poll awaits it, so a turn queued behind the
   * poll never fires `runTurn` against a torn-down engine (the stream hasn't
   * started yet, so `closeDrawer`'s abort would be a no-op there).
   *
   * REPLACED (not just aborted) by `onMount` when a remount reuses this
   * instance — see that method for the bug that fix retires. Every reader
   * therefore takes `.signal` at CALL time, never at construction time.
   *
   * Public for SPEC REACH only (the gate specs abort it to simulate an
   * unmount); nothing outside this class touches it in app code — the
   * component goes through `dispose()`.
   */
  engineAbortController = new AbortController();

  /**
   * Dispose-previous slot for a resolve-first build's landing narration
   * collector (seeded-build-narration.ts): a re-clicked build chip must
   * supersede the prior collector. Mirrors the bridge's collector ref;
   * ref-shaped so `beginSeededBuildNarration` can consume it unchanged.
   */
  readonly seededBuildCollectorSlot: { current: NarrationCollector | null } = {
    current: null,
  };

  /**
   * The pending unsaved-changes prompt's resolver while the in-app dialog is
   * open. Instance-owned (not component state) so `dispose()` can settle it
   * as "stay" and the awaiting guard never hangs past the engine's lifetime;
   * the component mirrors only "is a dialog open" as render state. A second
   * guard while one dialog is open settles the FIRST as "stay" (it can no
   * longer be answered) and takes over the dialog — see the component's
   * `confirmLeaveViaDialog`.
   */
  readonly leaveConfirmSlot: {
    current: { resolve: (leave: boolean) => void } | null;
  } = { current: null };

  /**
   * Single-flight for `switchGeographyTaxonomy`: its body is a preference
   * write plus a network round trip, and the render-scoped guards lag a rapid
   * double-click (mirrors the nav-offer launch lock).
   */
  private geoTaxSwitchInFlight = false;

  /**
   * Fresh-per-render dep set, replaced whole by the render body (H1). `null`
   * only between construction and the first render-body `updateDeps`; no
   * published method can run in that window (the instance is not yet in the
   * store), so `d` treats a null read as a programming error.
   */
  private deps: AssistantEngineDeps | null = null;

  /**
   * Per-channel drain watermarks for `consumeFilterApplyFailures` /
   * `consumeFilterApplyAdjustments`. Seeded at CONSTRUCTION — i.e. the first
   * engine render, exactly when the `useRef(channel.seq)` initializers they
   * replace ran — so an event published before THIS engine existed (the
   * command store is module-global and outlives any surface) is never replayed
   * into a fresh transcript. Deliberately NOT re-seeded in `onMount`: keeping
   * the higher watermark across a dispose→remount can only under-replay, which
   * is the safe direction.
   */
  private seenAdjustmentSeq: number;

  constructor(stable: AssistantEngineStableRefs) {
    this.stable = stable;
    const { channels } = useGlobalAiCommandStore.getState();
    this.seenAdjustmentSeq = channels.filterApplyAdjustment.seq;
  }

  /**
   * Replace the dep set. MUST be called from the render body (H1) with the
   * FULL set — the runner hook runs first in the render body, so `runTurn` is
   * available for the single call. Replace, not merge: a dep a render stops
   * supplying must not silently keep the previous render's value.
   */
  updateDeps(next: AssistantEngineDeps): void {
    this.deps = next;
  }

  /**
   * Called from the component's mount effect — RE-ARMS the engine-abort
   * controller when a previous mount left it aborted.
   *
   * The controller this replaced was lazy-initialized in the render body and
   * aborted in the unmount cleanup, but nothing ever re-armed it. Any remount
   * that reuses the same instance — StrictMode's simulated mount → unmount →
   * mount, which the app root doesn't enable but RTL does — therefore left a
   * permanently aborted signal behind, and every subsequent gated turn saw
   * `signals.some(aborted)` immediately after the taxonomy hold: `runTurn`
   * never fired and the chat silently never answered (the abort is the
   * engine's, so not even a "Stopped" marker appeared). Re-arming here makes
   * mount the single point that decides the engine is live — which is also
   * why it clears `disposed`: mount and dispose are the only two writers of
   * "is this instance live", and they must compose in either order.
   */
  onMount(): void {
    this.disposed = false;
    if (this.engineAbortController.signal.aborted) {
      this.engineAbortController = new AbortController();
    }
  }

  /**
   * Unmount teardown, called from the component's effect cleanup. Reproduces
   * the pre-extraction cleanup exactly:
   *
   * - abort the engine controller, so a turn queued behind the taxonomy-hold
   *   poll can never fire `runTurn` against a torn-down engine;
   * - NULL the store's `api` field. This is a hard contract, not tidiness:
   *   the skins `?.`-guard every call on it, and
   *   `use-global-ai-conversation-persistence` reads `api !== null` as its
   *   "engine mounted" signal (it gates the conversation-list query). Leaving
   *   a disposed instance registered would keep both believing the engine is
   *   live;
   * - reset the turn/continuation/error/launch-key fields. The `turn: null`
   *   write IS the truncated-turn autosave signal: a turn cut short by this
   *   unmount (navigation to a hidden path) transitions running→idle through
   *   it, which is the edge the persistence subscription saves on.
   *
   * `pendingContinuation` dies with the engine, exactly as the component-local
   * refs it replaced did.
   */
  dispose(): void {
    this.disposed = true;
    this.engineAbortController.abort();
    // Settle any pending leave-confirm as "stay" so the awaiting guard never
    // hangs past the engine's lifetime.
    this.leaveConfirmSlot.current?.resolve(false);
    this.leaveConfirmSlot.current = null;
    useGlobalAiEngineStore.setState({
      api: null,
      turn: null,
      pendingContinuation: null,
      error: null,
      reportOfferLaunchingKey: null,
    });
  }

  /**
   * Published-method guard: true once disposed, with a dev-only warn so a
   * late call is visible while developing but silent in production. See the
   * `disposed` field for why a no-op (not a throw) is the right posture.
   */
  private isDisposed(method: string): boolean {
    if (!this.disposed) {
      return false;
    }
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `Global assistant engine: ${method}() called after unmount — ignored.`
      );
    }
    return true;
  }

  /**
   * Dep access for the methods below. Throws (loudly, with the method name
   * unavailable but the stack intact) if a method runs before the first
   * render-body `updateDeps` — a programming error in the app, and in specs
   * the signal that a fixture forgot to supply the deps.
   */
  private get d(): AssistantEngineDeps {
    if (this.deps === null) {
      throw new Error(
        'AssistantEngine: a method ran before updateDeps() supplied its deps'
      );
    }
    return this.deps;
  }

  /** Command-store emit, read imperatively — module-level store access is safe from async callbacks. */
  private emit(command: GlobalAiCommand): void {
    useGlobalAiCommandStore.getState().emit(command);
  }

  /**
   * Entity type for the CURRENT route, derived at call time. Every consumer
   * is a click/turn-time callback, so a live derivation can never go stale
   * mid-turn the way a render-closure could.
   */
  private currentPathEntity(): GlobalAssistantEntityType {
    return inferEntityTypeFromNavigatePath(this.d.getCurrentRoute(), 'company');
  }

  // --- Leaf emit/resolve cluster (the turn-runner's dep set) --------------

  /**
   * `{ activitiesEnabled: true }` when the pages of `entityType` hold the
   * ACTIVITY filter, by FilterProvider's own rule (`isActivityFilterOnForPrimary`);
   * absent otherwise, which every consumer reads as "no". One place, so the
   * turn-step context, both route-filter resolutions, the refinement pass and
   * headless builds never disagree. The flags fold in Unleash readiness, but an
   * Activity page's primary IS the filter, so it never waits on them.
   */
  private activitiesEnabledOption(entityType: GlobalAssistantEntityType): {
    activitiesEnabled?: true;
  } {
    const analyticsEntity = toCustomDashAssistantEntityType(entityType);
    return isActivityFilterOnForPrimary(
      this.d.activityFilterFlags,
      analyticsEntity ? PRIMARY_FILTER_BY_AI_ENTITY[analyticsEntity] : undefined
    )
      ? { activitiesEnabled: true }
      : {};
  }

  /**
   * What a headless page build for `entityType` resolves its filters under:
   * that entity's taxonomies and activity capability. Read for the BUILD's
   * entity, not the route's — an Activity dashboard built from a Company page
   * still needs activity extraction. An adaptive preference's taxonomy is only
   * the last page's residue, and the page-stream path has no adaptive flag to
   * re-derive it, so it is left to the server default.
   */
  readonly headlessBuildTaxonomy = (
    entityType: CustomDashAssistantEntityType
  ): HeadlessBuildTaxonomy => {
    const taxonomy = this.d.taxonomyByEntity(entityType);
    return {
      rolesTax:
        taxonomy.rolesKey === 'adaptive' ? undefined : taxonomy.rolesTax,
      skillsTax:
        taxonomy.skillsKey === 'adaptive' ? undefined : taxonomy.skillsTax,
      activitiesTax:
        taxonomy.activitiesKey === 'adaptive'
          ? undefined
          : taxonomy.activitiesTax,
      geoTax: taxonomy.geoTax,
      ...this.activitiesEnabledOption(entityType),
    };
  };

  /**
   * Shared resolve+emit core for the engine's two route-filter paths (the
   * drawer/chip path via `resolveThenEmitRouteFilters` and the loop runner's
   * `resolveAndEmitRouteFilters` dep): resolve the prompt into a filter
   * artifact under `taxonomy` and emit the destination's `applyRouteFilters`
   * command. The component wraps it with status handling, tracking, and the
   * adaptive-taxonomy refinement pass; the runner dep is this core alone.
   */
  readonly resolveRouteFiltersAndEmitApply = async (args: {
    requestId: string;
    path: string;
    promptForFilters: string;
    entityType: GlobalAssistantEntityType;
    taxonomy: AssistantTaxonomy;
    bypassCache?: boolean;
    merge?: boolean;
    /**
     * The geography taxonomy this resolution was FORCED under, when a caller
     * changed it — see `applyRouteFilters.adoptGeoTax`. Distinct from
     * `taxonomy.geoTax`, which is whatever the resolution happened to run
     * with: only a deliberate switch sets this, and only then may the
     * destination page re-key its own taxonomy filter.
     */
    adoptGeoTax?: number;
    signal?: AbortSignal;
  }): Promise<GlobalAssistantResolvedRouteFilters> => {
    const {
      requestId,
      path,
      promptForFilters,
      entityType,
      taxonomy,
      bypassCache,
      merge,
      adoptGeoTax,
      signal,
    } = args;
    const filterKind = getGlobalAssistantRouteFilterKind(path);
    const resolved = await getRouteFilterResponse(promptForFilters, taxonomy, {
      filterKind,
      screenerEntityType: toScreenerAssistantEntityType(entityType),
      // Read here, once, rather than threaded by every caller: a caller that
      // forgot the flag would silently resolve without activity extraction.
      ...this.activitiesEnabledOption(entityType),
      ...(bypassCache ? { bypassCache: true } : {}),
    });
    // The resolve above is a multi-second round trip, so the turn can be
    // stopped inside it. Emitting past that point applies filters to the
    // destination page for a turn the user cancelled — and once the bridge
    // claims the command there is no rewind, exactly as in the navigation
    // branch of the `apply_filters` handle. Returning the resolution unemitted
    // is enough: the tool's own post-await check turns the turn into a no-op.
    if (signal?.aborted) {
      return resolved;
    }
    this.emit({
      kind: 'applyRouteFilters',
      requestId,
      ...resolved,
      targetPath: path,
      entityType,
      prompt: promptForFilters,
      ...(merge ? { merge: true } : {}),
      ...(adoptGeoTax !== undefined ? { adoptGeoTax } : {}),
    });
    return resolved;
  };

  readonly resolveAndEmitRouteFilters = async (args: {
    requestId: string;
    path: string;
    promptForFilters: string;
    entityType: GlobalAssistantEntityType;
    signal?: AbortSignal;
  }): Promise<TalentDiscoveryAiFilterSearchResponseV2> => {
    const resolved = await this.resolveRouteFiltersAndEmitApply({
      ...args,
      taxonomy: this.d.taxonomyByEntity(args.entityType),
    });
    // The loop's apply_filters digest builder consumes the TD v2 shape; for
    // screener resolutions the response shape differs but the drawer still
    // applies it via the command. Cast to the TD type the runner expects.
    return resolved.response as TalentDiscoveryAiFilterSearchResponseV2;
  };

  readonly emitResolvedRouteFilters = (args: {
    requestId: string;
    path: string;
    response:
      | TalentDiscoveryAiFilterSearchResponseV2
      | CompanyDiscoveryAiOutput;
    prompt: string;
    entityType: GlobalAssistantEntityType;
    merge?: boolean;
    /**
     * See the `applyRouteFilters` command docblock — pairing is default-on,
     * so this only needs to be passed to explicitly opt OUT (`false`).
     * `undefined` (the common case) is forwarded as-is and defaults to
     * pairing at the command-store layer.
     */
    pairTaxonomy?: boolean;
  }): void => {
    const {
      requestId,
      path,
      response,
      prompt,
      entityType,
      merge,
      pairTaxonomy,
    } = args;
    // The `search_filters` server tool already resolved these filters into the
    // given response; emit the apply command directly with NO AI re-resolution.
    // Derive the apply kind from the path (screener vs tdV2) so company
    // discovery artifacts route to the bridge's screener applier — mirroring
    // `resolveThenEmitRouteFilters` / `resolveAndEmitRouteFilters`.
    const filterKind = getGlobalAssistantRouteFilterKind(path);
    const resolvedRouteFilters: GlobalAssistantResolvedRouteFilters =
      filterKind === 'screener'
        ? { filterKind, response: response as CompanyDiscoveryAiOutput }
        : {
            filterKind,
            response: response as TalentDiscoveryAiFilterSearchResponseV2,
          };
    this.emit({
      kind: 'applyRouteFilters',
      requestId,
      ...resolvedRouteFilters,
      targetPath: path,
      entityType,
      prompt,
      ...(merge ? { merge: true } : {}),
      // Forward `false` too — dropping it here would silently re-default to
      // pairing (default-on at the command-store layer) and make an
      // explicit opt-out unreachable.
      ...(pairTaxonomy !== undefined ? { pairTaxonomy } : {}),
    });
  };

  readonly offerReportFromLoop = (
    reportTypes: string[],
    prompt: string
  ): { reportNames: string[]; messageId?: string } => {
    const uniqueReportTypes = [...new Set(reportTypes)];
    // Resolve EACH requested report type to its builder config, skipping any
    // type we can't resolve (don't crash / don't render an empty chip).
    const offers = uniqueReportTypes.flatMap((reportType) => {
      const reportConfig = this.d.reportBuilderConfig[reportType];
      if (!reportConfig) {
        return [];
      }
      const description =
        reportConfig.description ??
        getGlobalAssistantReportCatalogEntry(
          reportType as Parameters<
            typeof getGlobalAssistantReportCatalogEntry
          >[0]
        )?.description;
      return [
        {
          reportType,
          reportName: reportConfig.label ?? reportType,
          sampleLink: reportConfig.sampleLink ?? '',
          confidence: 1,
          ...(description ? { description } : {}),
        },
      ];
    });

    if (offers.length === 0) {
      return { reportNames: [] };
    }

    mixpanel.track(
      BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_REPORT_OFFERED,
      {
        global_assistant_report_type: offers
          .map((offer) => offer.reportType)
          .join(','),
        global_assistant_report_offer_count: offers.length,
      }
    );
    const messageId = crypto.randomUUID();
    this.d.appendTranscript({
      id: messageId,
      role: 'assistant',
      kind: 'report_offers',
      offers,
      prompt,
    });
    return { reportNames: offers.map((offer) => offer.reportName), messageId };
  };

  /**
   * Enforce the SAME route-access/entitlement gate the manual chip uses and
   * report navigation honestly (no_access / unknown_page / cancelled) so the
   * loop never narrates a phantom navigation.
   *
   * Do NOT forward lastUserPrompt as resolvedPromptForApply: the loop's
   * `navigate` tool is pure navigation. Attaching the prompt would cause
   * getDiscoveryApplyPrompt to resolve a non-empty string for discovery
   * pages, triggering an unintended runDiscoveryAiSearchAfterNavigate
   * command that applies stale filters from the previous turn.
   */
  readonly navigateToOptionId = async (
    optionId: string
  ): Promise<NavigateToOptionResult> =>
    resolveNavigateToOptionId(optionId, this.d.userTabs, (match) =>
      this.navigateToMatch(match, {
        standard: true,
        skipFilterApply: true,
        trigger: 'tool',
      })
    );

  // Live route read — mirrors `emitCreateCurrentPage` (this pair used to
  // disagree: update read a render-closure pathname while create read
  // window.location, the same need under two conventions).
  //
  // PAYLOAD FRESHNESS DELTA (small — and NOT the one an earlier version of
  // this comment claimed): the conversation payload is now built from the
  // transcript in the STORE AT CALL TIME, where it used to be built from the
  // transcript of the engine's last COMMITTED render.
  //
  // It was never the turn-START transcript. `runTurn` does snapshot its deps
  // once, but these two page-gen sites deliberately bypass that snapshot and
  // read `depsRef.current.emitUpdateCurrentPage` / `emitCreateCurrentPage`
  // live (use-global-ai-turn-runner.ts, with comments saying so) — and the old
  // closure's `transcript` came from the assistant context, which SUBSCRIBES
  // to the conversation-state store, so every append re-rendered the engine
  // and installed a fresher closure.
  //
  // Consequence: same-turn messages — a read-grounded answer, a rendered
  // chart, an ask_user clarification — ALREADY reached the page-gen model
  // before this refactor. Every tool step awaits the network, so React had
  // always committed by then. All that changed is an append React has not
  // flushed yet: a sub-commit window. Do NOT attribute a page-gen output diff
  // to this change.
  readonly emitUpdateCurrentPage = (
    requestId: string,
    prompt: string
  ): void => {
    const matchedEntityType = this.inferEntityTypeForPath(
      this.d.getCurrentRoute()
    );
    const entityType =
      toCustomDashAssistantEntityType(matchedEntityType) ?? 'company';
    const fullConversationContext = buildBoundedConversationContext({
      transcript: this.d.getTranscript(),
      summary: this.d.conversationSummary,
    });
    const conversationPayload = toConversationPayload(fullConversationContext);
    this.emit({
      kind: 'startCustomPageStream',
      requestId,
      prompt,
      entityType,
      mode: 'update',
      ...(conversationPayload ? { conversation: conversationPayload } : {}),
    });
    this.d.setGenerationStatus('Updating page…');
  };

  /**
   * Build-here: emit a CREATE page-gen for the CURRENT draft (no nav, no new
   * draft). Used by the confirm-navigate-resume flow, where the chip click
   * already created the empty draft + navigated to it. The bridge's create
   * handler matches on `editMode && targetPath === current path` and
   * dispatches the generated layout into the (empty) current tab, so passing
   * the current path here populates this draft in place — no `createPageDraft`
   * / `navigate`. CREATE mode (vs update) keeps the build framed as a fresh
   * dashboard for the model's reasoning + grounded narration.
   *
   * Carries the same PAYLOAD FRESHNESS DELTA as `emitUpdateCurrentPage` — see
   * that method's comment: the transcript now comes from the store at call
   * time rather than from the engine's last committed render, a sub-commit
   * window. Same-turn assistant messages reached the page-gen model before
   * this refactor too, so this is NOT a page-gen output change.
   */
  readonly emitCreateCurrentPage = (
    requestId: string,
    prompt: string
  ): void => {
    const matchedEntityType = this.inferEntityTypeForPath(
      this.d.getCurrentRoute()
    );
    const entityType =
      toCustomDashAssistantEntityType(matchedEntityType) ?? 'company';
    const fullConversationContext = buildBoundedConversationContext({
      transcript: this.d.getTranscript(),
      summary: this.d.conversationSummary,
    });
    const conversationPayload = toConversationPayload(fullConversationContext);
    this.emit({
      kind: 'startCustomPageStream',
      requestId,
      prompt,
      entityType,
      mode: 'create',
      targetPath: this.d.getCurrentRoute(),
      ...(conversationPayload ? { conversation: conversationPayload } : {}),
    });
    this.d.setGenerationStatus('Designing your dashboard…');
  };

  /**
   * One unified outcome event per client-tool execution — the discriminator
   * answers "what did the assistant produce" (chart preview vs navigation
   * offer vs build offer vs …) and joins to interactions via
   * conversation_id + turn_index.
   */
  readonly onToolOutcome = (
    toolName: string,
    { isTextAnswer }: { isTextAnswer: boolean }
  ): void => {
    const OUTCOME_TYPE_BY_TOOL: Record<string, string> = {
      navigate: 'page_navigation_offer',
      apply_filters: 'filter_routing',
      create_custom_page: 'custom_page_offer',
      offer_page_or_custom: 'page_or_custom_offer',
      offer_standard_page: 'standard_page_offer',
      update_current_page: 'page_update',
      offer_report: 'report_offer',
      ask_user: 'clarification',
    };
    const chartOutcome = isTextAnswer ? 'text_answer' : 'chart_preview';
    const outcomeType =
      toolName === 'render_chart'
        ? chartOutcome
        : (OUTCOME_TYPE_BY_TOOL[toolName] ?? toolName);
    mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_OUTCOME, {
      outcome_type: outcomeType,
      tool_name: toolName,
      conversation_id: this.stable.conversationTrackingIdRef.current,
      turn_index: countUserTurns(this.d.getTranscript()),
      // Splits a turn from its silent continuation, which shares turn_index.
      turn_id: useGlobalAiEngineStore.getState().turn?.turnId ?? null,
      entity_type: this.currentPathEntity(),
      page_path: this.d.getCurrentRoute(),
    });
  };

  // Live browser path, never a turn-start snapshot — the runner captures
  // `deps` once at turn start, and a mid-turn navigation must not leave
  // apply_filters targeting the page we LEFT (the full rationale lives on
  // `getCurrentRoute`).
  readonly currentPageLabel = (): string => this.d.getCurrentRoute();

  readonly inferEntityTypeForPath = (path: string): GlobalAssistantEntityType =>
    inferEntityTypeFromNavigatePath(
      path,
      this.d.sessionEntityScope ?? this.currentPathEntity()
    );

  readonly buildTurnContext = (): TurnStepContextFields => {
    // Mirror the legacy `suggest`/pipeline context assembly: page-level
    // taxonomy for the current route's entity, the current-page descriptor,
    // and the custom-dash edit-mode flags. Only include fields we can fill.
    // One live route read for the whole context so every field describes
    // the SAME page even if a navigation lands mid-call.
    const pathname = this.d.getCurrentRoute();
    const entityType = this.inferEntityTypeForPath(pathname);
    const taxonomy = this.d.taxonomyByEntity(entityType);
    const customDashContext = this.stable.customDashAssistContextRef.current;
    // Read live before each step; React subscriptions may still describe the
    // page before an in-turn filter application.
    const { lastAppliedFilterContextByPath, currentPageFiltersByPath, queue } =
      useGlobalAiCommandStore.getState();
    const currentPage = buildGlobalAssistantCurrentPageContext({
      pathname,
      isInCustomDashEditMode: customDashContext.isInCustomDashEditMode,
      ...(lastAppliedFilterContextByPath[pathname]
        ? {
            lastAppliedFilterContext: lastAppliedFilterContextByPath[pathname],
          }
        : {}),
    });
    const canFilter = isGlobalAssistantCurrentPageFilterable({
      pathname,
      isInCustomDashEditMode: customDashContext.isInCustomDashEditMode,
    });
    // Only from a page that can filter: on a page whose filters do not scope
    // what it shows, layering them under the ask would narrow an answer by
    // something the user is not looking at.
    const pageFilters =
      canFilter && !queue.some(({ command }) => changesPageScope(command))
        ? currentPageFiltersByPath[pathname]
        : undefined;
    const currentPageFields: {
      pageKind?: string;
      canFilter?: boolean;
      discoveryKind?: GlobalAssistantDiscoveryKind;
      baseFilterPrompt?: string;
      matchedOptionId?: string;
      scope?: CompositionPageScope;
    } = {
      pageKind: currentPage.kind,
      canFilter,
      // One field, so the filters a page carries and the names of the ones it
      // could not travel with cannot be sent under keys that drift apart.
      ...(pageFilters ? { scope: pageFilters } : {}),
      ...(currentPage.kind === 'discovery'
        ? { discoveryKind: currentPage.discoveryKind }
        : {}),
      ...('baseFilterPrompt' in currentPage && currentPage.baseFilterPrompt
        ? { baseFilterPrompt: currentPage.baseFilterPrompt }
        : {}),
      // The page's own `__std__:` id, so the loop can recognise that a page it
      // would offer is the one already on screen and filter it in place
      // instead of handing back a chip to where the user already is (#6402).
      ...('matchedOptionId' in currentPage && currentPage.matchedOptionId
        ? { matchedOptionId: currentPage.matchedOptionId }
        : {}),
    };
    // Surface only the standard pages this account can open: the BE strips
    // these denied ids from the navigate candidate enum and the pages grid,
    // so the model never proposes or calls an inaccessible page.
    const deniedNavigateOptionIds = getDeniedStandardNavigateOptionIds(
      this.d.userTabs
    );
    return {
      ...(taxonomy.rolesTax ? { rolesTax: taxonomy.rolesTax } : {}),
      ...(typeof taxonomy.skillsTax === 'number'
        ? { skillsTax: taxonomy.skillsTax }
        : {}),
      // Adaptive preference: rolesTax/skillsTax above are only the stored
      // last-resolved fallback, so the server-side filter search must
      // re-derive the taxonomy from the prompt's own company.
      ...(taxonomy.rolesKey === 'adaptive' ? { rolesTaxAdaptive: true } : {}),
      ...(taxonomy.skillsKey === 'adaptive' ? { skillsTaxAdaptive: true } : {}),
      ...(typeof taxonomy.activitiesTax === 'number'
        ? { activitiesTax: taxonomy.activitiesTax }
        : {}),
      ...(taxonomy.activitiesKey === 'adaptive'
        ? { activitiesTaxAdaptive: true }
        : {}),
      // Only when definitively true, same rule as `isTrialUser` below: an
      // unresolved flag reads `false` and a bootstrap-cached `true` can beat
      // `flagsReady`, so `activityFilterFlags` folds in readiness and only a
      // confirmed-on flag is sent — the one owning the current route's
      // entity. Absent means "no" server-side, so a turn fired mid-hydration
      // runs without activity extraction rather than claiming a filter the
      // page has not grown yet; an Activity page is the exception, as its
      // primary is that filter.
      ...this.activitiesEnabledOption(entityType),
      activityFilterFlags: this.d.activityFilterFlags,
      // Geography taxonomy preference (metro 1 / admin 2). When absent, the
      // server resolves geography under metro.
      ...(typeof taxonomy.geoTax === 'number'
        ? { geoTax: taxonomy.geoTax }
        : {}),
      // Only when definitively true: `isTrialUser` is `null` while the
      // logged-in user resolves, and a still-loading value must never be
      // read server-side as a confirmed paid account.
      ...(this.d.isTrialUser === true ? { isTrialUser: true } : {}),
      currentPage: currentPageFields,
      customDash: {
        inCustomDashEditMode: customDashContext.isInCustomDashEditMode,
        hasCurrentPageItems: customDashContext.hasCurrentPageItems,
      },
      ...(deniedNavigateOptionIds.length > 0
        ? { deniedNavigateOptionIds }
        : {}),
    };
  };

  // --- Turn gate cluster -------------------------------------------------

  /**
   * Hold a turn start until the taxonomy preferences have hydrated (bounded).
   * A cold session that submits instantly — the assistant homepage's example
   * prompts make this a one-click race — would otherwise build its turn
   * context from DEFAULT preferences and the backend would resolve roles
   * under the default taxonomy (wrong role → empty chart). If preferences
   * never arrive (endpoint down), fall through after the timeout: degraded
   * resolution beats a chat that never answers.
   */
  private readonly waitForTaxonomyReady = async (
    signals: AbortSignal[]
  ): Promise<void> => {
    const TIMEOUT_MS = 4000;
    const POLL_MS = 50;
    const startedAt = Date.now();
    // `this.d.isTaxonomyReady` is re-read on EVERY iteration, so the poll sees
    // each render's fresh value — the only thing the `isTaxonomyReadyRef`
    // mirror this replaced was ever for.
    while (
      !this.d.isTaxonomyReady &&
      !signals.some((s) => s.aborted) &&
      Date.now() - startedAt < TIMEOUT_MS
    ) {
      await new Promise((resolve) => setTimeout(resolve, POLL_MS));
    }
  };

  /**
   * Shared turn gate for `submitPrompt` / `startAssistantTurn`: begin the
   * keyed turn record, hold the start behind the taxonomy wait (cancellable),
   * run the turn, and ALWAYS end the record. Synchronous up to the first
   * await, so `beginTurn` + the controller seed land before the caller
   * returns (the drawer's isTurnRunning gate depends on that).
   *
   * Public for SPEC REACH only — the gate matrix drives it directly rather
   * than through `submitPrompt`/`startAssistantTurn`. It is NOT published API
   * (it carries no disposed guard; its two callers do).
   */
  readonly runGuardedTurn = async (
    prompt: string,
    options?: { suppressReportOffer?: boolean },
    // Passed in when the caller already tracked the prompt under this id.
    turnId: string = crypto.randomUUID()
  ): Promise<void> => {
    beginTurn(turnId, Date.now());
    // Seed the shared turn controller BEFORE the taxonomy wait so a stop
    // pressed in that window (up to 4s, before the runner opens its own
    // controller) has something to abort. The runner replaces this with its
    // own controller when it starts, which is harmless — by then this wait
    // has already settled.
    const gateController = new AbortController();
    this.stable.turnAbortRef.current = gateController;
    // Read the engine controller HERE, not at construction: `onMount` swaps
    // the instance on a remount, and a signal captured earlier would be the
    // dead one.
    const signals = [this.engineAbortController.signal, gateController.signal];
    try {
      await this.waitForTaxonomyReady(signals);
      // Either the engine unmounted while we waited (e.g. navigate to a
      // hidden path) or the user pressed stop — don't fire the turn.
      if (signals.some((s) => s.aborted)) {
        if (gateController.signal.reason === USER_STOP_ABORT_REASON) {
          this.d.appendTranscript(createStoppedMessage());
        }
        return;
      }
      await this.d.runTurn(prompt, options);
    } finally {
      // Keyed: if this engine instance was replaced and a NEW turn is live,
      // this is a structural no-op (the remount race that used to re-enable
      // the input mid-turn). The idle transition it produces is the
      // turn-end autosave's signal.
      endTurn(turnId);
    }
  };

  /**
   * The drawer's old `handleSubmit`, with the draft text passed in by the
   * skin. Returns false synchronously when rejected so the skin keeps the
   * user's draft; on true the skin clears its textarea and the turn runs.
   */
  readonly submitPrompt = (rawPromptInput: string): boolean => {
    if (this.isDisposed('submitPrompt')) return false;
    const rawPrompt = rawPromptInput.trim();
    const engineState = useGlobalAiEngineStore.getState();
    if (
      !rawPrompt ||
      engineState.reportOfferLaunchingKey !== null ||
      selectIsTurnRunning(engineState)
    ) {
      return false;
    }
    // Live transcript read: a submit is a click-time call, so the store holds
    // the render value plus anything appended since — which is what the
    // clarification classification below should see.
    const transcript = this.d.getTranscript();
    setEngineError(null);
    // Clarification replies are classified by the router LLM via
    // conversation history + pendingClarification + pendingClarificationResolution.
    const lastClarification = findLatestClarification(transcript);
    const promptMode = lastClarification ? 'clarification_answer' : 'generate';
    const turnId = crypto.randomUUID();
    trackAIAssistantPrompt({
      assistantSurface: 'global',
      conversationId: this.stable.conversationTrackingIdRef.current,
      turnIndex: countUserTurns(transcript) + 1,
      turnId,
      promptText: rawPrompt,
      promptMode,
      entityType: this.currentPathEntity(),
      pagePath: this.d.getCurrentRoute(),
    });
    this.d.setLastUserPrompt(rawPrompt);
    this.d.appendTranscript({
      id: crypto.randomUUID(),
      role: 'user',
      text: rawPrompt,
    });
    void this.runGuardedTurn(rawPrompt, undefined, turnId);
    return true;
  };

  /**
   * Programmatically start an assistant turn seeded with `prompt`, reusing the
   * exact same machinery as `submitPrompt` (append a user message, guard
   * `isTurnRunning`, run the turn). Used by the nav-confirm chip to RESUME a
   * chained task after the user confirms navigation. Bails if a turn is
   * already running so a click can't stack overlapping turns.
   */
  readonly startAssistantTurn = async (
    prompt: string,
    options?: {
      /**
       * Silent seed: run a REAL turn (the model receives `prompt` as the turn's
       * `userPrompt`) but DON'T render the seed as a chat bubble. Used by the
       * confirm-navigate-resume flows, whose seed is a bracketed `[System: …]`
       * instruction the user must never see. `runTurn` passes `prompt` to the
       * loop as `userPrompt` SEPARATELY from `priorMessages` (built from the
       * transcript), so the model still gets the seed without the transcript
       * append. We also skip `setLastUserPrompt` so the bracketed instruction
       * never becomes the prompt a later chip (e.g. "build the dashboard")
       * resolves from. The turn is still tracked + guarded as a normal turn.
       */
      silent?: boolean;
      /**
       * Skip the post-loop report ride-along for this turn. Used by the build
       * resume: a matching report was already offered alongside the Build
       * button, so re-offering it after the build is noise.
       */
      suppressReportOffer?: boolean;
    }
  ): Promise<void> => {
    if (this.isDisposed('startAssistantTurn')) return;
    const trimmed = prompt.trim();
    if (!trimmed || selectIsTurnRunning(useGlobalAiEngineStore.getState())) {
      return;
    }
    const turnId = crypto.randomUUID();
    if (!options?.silent) {
      // Non-silent resume turns are tracked + rendered like a normal prompt.
      // Silent turns are system-seeded continuations (e.g. the build resume's
      // "[System: …]" instruction): the user's real prompt was already tracked
      // when they first submitted it, so we neither re-track it (avoiding a
      // double-count) nor log the 250+ char seed as a user utterance — and it
      // never renders as a chat bubble. The entity/page reads are live, so
      // they reflect the DESTINATION page (this runs after the navigation).
      trackAIAssistantPrompt({
        assistantSurface: 'global',
        conversationId: this.stable.conversationTrackingIdRef.current,
        turnIndex: countUserTurns(this.d.getTranscript()) + 1,
        turnId,
        promptText: trimmed,
        promptMode: 'generate',
        entityType: this.currentPathEntity(),
        pagePath: this.d.getCurrentRoute(),
      });
      this.d.setLastUserPrompt(trimmed);
      this.d.appendTranscript({
        id: crypto.randomUUID(),
        role: 'user',
        text: trimmed,
      });
    }
    await this.runGuardedTurn(
      trimmed,
      { suppressReportOffer: options?.suppressReportOffer },
      turnId
    );
  };

  readonly cancelTurn = (): void => {
    if (this.isDisposed('cancelTurn')) return;
    this.stable.turnAbortRef.current?.abort(USER_STOP_ABORT_REASON);
    this.stable.turnAbortRef.current = null;
    this.stable.streamAbortRef.current?.abort(USER_STOP_ABORT_REASON);
    this.stable.streamAbortRef.current = null;
    // Release the report/page launch lock so the input re-enables. The
    // launch itself is a short navigate with no abort signal, so a stop
    // pressed during it frees the UI but cannot un-navigate.
    setReportOfferLaunchingKey(null);
    // `stopping` is still "running" to every derived reader, so this write
    // cannot fire the turn-end autosave early: only `endTurn` (called from
    // the turn's `finally`, AFTER the runner appended its "Stopped" marker)
    // produces the idle transition the autosave subscribes to. This replaces
    // the old comment-enforced protocol where cancelTurn had to deliberately
    // NOT clear the running flag to keep the autosave from snapshotting a
    // transcript without the Stopped marker.
    const live = useGlobalAiEngineStore.getState().turn;
    if (live) {
      markTurnStopping(live.turnId);
    }
  };

  readonly resetSession = (): void => {
    if (this.isDisposed('resetSession')) return;
    const previousTurnCount = countUserTurns(this.d.getTranscript());
    // Only a reset that abandons a real conversation is worth recording.
    if (previousTurnCount > 0) {
      mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_NEW_CHAT, {
        previous_conversation_id: this.stable.conversationTrackingIdRef.current,
        previous_turn_count: previousTurnCount,
        page_path: this.d.getCurrentRoute(),
      });
    }
    this.d.clearTranscript();
    this.stable.conversationTrackingIdRef.current = crypto.randomUUID();
    setEngineError(null);
    this.d.setLastUserPrompt(null);
  };

  // --- Route-filter + navigation cluster ---------------------------------

  /**
   * The unsaved-changes redirect guard, evaluated against the LIVE route.
   * Every caller is a click handler, so the browser path is still the page
   * the user is looking at when this runs. ASYNC: the guard can open the
   * in-app unsaved-changes dialog and awaits the user's answer.
   */
  readonly confirmAssistantRedirect = (targetPath?: string) =>
    confirmGlobalAiAssistantRedirect({
      customDashContext: this.stable.customDashAssistContextRef.current,
      currentPathname: this.d.getCurrentRoute(),
      targetPath,
      confirmLeave: this.d.confirmLeave,
    });

  /**
   * RESOLVE-FIRST (develop, meeting 2026-07-08): resolve the filters and emit
   * the apply command, returning whether resolution SUCCEEDED so a navigating
   * caller can await it before landing (no default-page flicker).
   */
  readonly resolveThenEmitRouteFilters = async (args: {
    path: string;
    promptForFilters: string;
    entityType: GlobalAssistantEntityType;
    requestId?: string;
    /**
     * Skip both prefetch tiers for this resolution. Used by the geography
     * taxonomy switch, where the cached entry for this prompt is precisely
     * the outgoing-taxonomy response we are replacing.
     */
    bypassCache?: boolean;
    /**
     * Force the geography taxonomy this resolution runs under, overriding the
     * preference-derived value. The taxonomy switch writes the preference and
     * re-resolves in the same tick, and the preference read only catches up a
     * render later — so the NEW id has to be threaded explicitly or the fresh
     * request goes out under the taxonomy the user just left.
     */
    geoTax?: number;
    /**
     * Apply ON TOP of the page's current filters instead of replacing them.
     * Set by the geography taxonomy switch, whose re-resolution runs only the
     * single ask that earned the offer and would otherwise drop every filter
     * an earlier turn established. See `GeographyTaxonomySwitchResolveArgs`.
     */
    merge?: boolean;
  }): Promise<boolean> => {
    const { path, promptForFilters, entityType } = args;
    const requestId = args.requestId ?? crypto.randomUUID();
    const filterKind = getGlobalAssistantRouteFilterKind(path);
    mixpanel.track(
      BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_QUEUED,
      {
        entity_type: entityType,
        global_assistant_route_filter_request_id: requestId,
      }
    );
    const RESOLVING_STATUS = 'Resolving filters…';
    this.d.setGenerationStatus(RESOLVING_STATUS);
    // Safety net: if no bridge ever claims this command (e.g. user
    // navigates elsewhere, or the target page has an incompatible
    // entity type), the queue entry expires at COMMAND_TTL_MS and the
    // "Resolving filters…" status would otherwise stay forever. Clear
    // it once the queue TTL has passed, but only if the status we set
    // is still the one on screen — a bridge may have legitimately
    // moved it to "Applying filters…" or an error, and we don't want
    // to wipe those.
    const clearIfStillResolving = setTimeout(() => {
      if (this.d.getGenerationStatus() === RESOLVING_STATUS) {
        this.d.setGenerationStatus(null);
      }
    }, COMMAND_TTL_MS);
    // Use whatever taxonomy is available right now — the store's
    // published value if any, or saved user prefs. For company-entity
    // + adaptive this may be wrong (adaptive resolution depends on the
    // target page's company filter, which the AI is about to apply).
    // The refinement pass below catches that case.
    const preferenceTaxonomy = this.d.taxonomyByEntity(entityType);
    const initialTaxonomy =
      args.geoTax === undefined
        ? preferenceTaxonomy
        : { ...preferenceTaxonomy, geoTax: args.geoTax };
    try {
      await this.resolveRouteFiltersAndEmitApply({
        requestId,
        path,
        promptForFilters,
        entityType,
        taxonomy: initialTaxonomy,
        ...(args.bypassCache ? { bypassCache: true } : {}),
        ...(args.merge ? { merge: true } : {}),
        // The SAME id that forced the resolution above, so the destination
        // page re-keys its taxonomy filter to match: the preference write
        // alone leaves the page rendering the tree the user just left, and
        // only this caller knows a switch happened.
        ...(args.geoTax !== undefined ? { adoptGeoTax: args.geoTax } : {}),
      });
      // Screener uses a different endpoint; do not run TD-only refinement.
      if (filterKind !== 'tdV2') return true;
      // TODO(backend-adaptive): DELETE this entire refinement block
      // once the AI filter search endpoint accepts "adaptive" as a
      // taxonomy value. (See the long-form rationale on develop.)
      //
      // Mostly dormant now that response pairing is default-on: the initial
      // `emit` above lands with `pairRoleTaxonomyWithResponse` on (unless
      // `pairTaxonomy: false` was explicitly passed), which pins the FE
      // taxonomy to the value the request ran under BEFORE this block's
      // `waitForTaxonomyChange` gets a chance to observe a change — so it
      // normally times out and no-ops. It still fires when the BACKEND
      // itself returns a *different* `roles_tax_id` than the one requested
      // (i.e. the FE's pre-apply guess of "adaptive" was wrong and the
      // backend corrected it) — that's a real post-apply taxonomy change,
      // and the pinned value from the first apply needs the refined one to
      // replace it. Kept as a safety net for that case pending the
      // backend-adaptive cleanup above.
      if (entityType !== 'company' && entityType !== 'talentDiscovery') {
        return true;
      }
      // FIRE-AND-FORGET (H7): this pass can emit its refined apply ~13s after
      // the click and is deliberately NOT bounded by the engine's lifetime —
      // the emit goes to the module-level command store, which outlives any
      // mount, so a refinement that lands after an unmount still reaches
      // whichever bridge is on the destination page.
      void (async () => {
        const COMMAND_COMPLETE_WAIT_MS = 10_000;
        const TAXONOMY_CHANGE_WAIT_MS = 3_000;
        const initialOutcome = await waitForCommandComplete(
          requestId,
          COMMAND_COMPLETE_WAIT_MS
        );
        // Only a real apply can produce a post-apply taxonomy change to refine
        // against; an expired/timed-out command means nothing landed.
        if (initialOutcome !== 'completed') return;
        const refinedTaxonomy = await waitForTaxonomyChange(
          entityType,
          initialTaxonomy,
          TAXONOMY_CHANGE_WAIT_MS
        );
        if (refinedTaxonomy === null) return;
        try {
          const refinedResolved = await getRouteFilterResponse(
            promptForFilters,
            {
              rolesTax: refinedTaxonomy.rolesTax,
              skillsTax: refinedTaxonomy.skillsTax,
              activitiesTax: refinedTaxonomy.activitiesTax,
              rolesKey: initialTaxonomy.rolesKey,
              skillsKey: initialTaxonomy.skillsKey,
              activitiesKey: initialTaxonomy.activitiesKey,
              // Only role/skill are refined by the post-apply pass; geography
              // is a preference, so it carries over unchanged — and it must,
              // or the refined response would be stored under the metro key.
              ...(typeof initialTaxonomy.geoTax === 'number'
                ? { geoTax: initialTaxonomy.geoTax }
                : {}),
            },
            {
              bypassCache: true,
              filterKind: 'tdV2',
              ...this.activitiesEnabledOption(entityType),
            }
          );
          this.emit({
            kind: 'applyRouteFilters',
            requestId: crypto.randomUUID(),
            ...refinedResolved,
            targetPath: path,
            entityType,
            prompt: promptForFilters,
            // Carries the caller's merge intent, or this refinement would
            // REPLACE what the initial merged apply just preserved — undoing
            // the fix for the geography taxonomy switch one apply later.
            ...(args.merge ? { merge: true } : {}),
          });
        } catch {
          // Refinement is best-effort; the initial apply has already
          // landed so the user has SOME filters applied.
        }
      })();
      return true;
    } catch (err) {
      clearTimeout(clearIfStillResolving);
      const message = err instanceof Error ? err.message : 'Failed';
      mixpanel.track(
        BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_FAILED,
        {
          entity_type: entityType,
          global_assistant_route_filter_error: message,
        }
      );
      this.d.setGenerationStatus(`Couldn't resolve filters: ${message}`);
      return false;
    }
  };

  /**
   * Set a status and schedule its own removal at the command TTL — only if
   * nothing else has replaced it by then. The two deterministic-apply paths
   * below share this: a bridge may legitimately have moved the status on to
   * "Applying filters…" or an error, and clearing THAT would wipe live state.
   */
  private setStatusWithTtlClear(status: string): void {
    this.d.setGenerationStatus(status);
    setTimeout(() => {
      if (this.d.getGenerationStatus() === status) {
        this.d.setGenerationStatus(null);
      }
    }, COMMAND_TTL_MS);
  }

  /**
   * Returns whether navigation actually proceeded. The redirect guard can
   * cancel (unsaved changes), in which case we report `navigated: false` so
   * loop-initiated callers don't narrate a navigation that never happened.
   * Existing chip callers ignore the return value.
   *
   * RESOLVE-FIRST (develop, meeting 2026-07-08): filter resolution is awaited
   * BEFORE navigating, so the destination mounts with its resolved apply
   * command already waiting — one landing, no default-page flicker. On
   * resolution failure we stay put and say so, rather than landing unfiltered
   * and masquerading as success.
   */
  readonly navigateToMatch = async (
    match: GlobalAssistantSuggestMatch,
    options?: {
      standard?: boolean;
      prompt?: string;
      skipFilterApply?: boolean;
      /**
       * ALREADY-resolved filter artifact to apply on the destination — the
       * exact `search_filters` response a single-value answer used. When
       * present, NO prompt-based re-resolution runs on this navigation: the
       * artifact is emitted directly as a resolved `applyRouteFilters`
       * command (replace semantics — a fresh landing) that the destination
       * bridge claims after the router lands. This is what keeps an
       * `offer:page` click deterministic: the page shows the SAME filter set
       * the answer was computed from, never an independent AI resolution of
       * the prompt (which can land on a different filter set entirely).
       */
      resolvedFilters?: TalentDiscoveryAiFilterSearchResponseV2;
      /** Defaults to `click`; `navigateToOptionId` (assistant-driven) passes `tool`. */
      trigger?: OptionTrigger;
      /** The answer whose link was clicked. */
      answerMessageId?: string;
    }
  ): Promise<{ navigated: boolean }> => {
    if (this.isDisposed('navigateToMatch')) return { navigated: false };
    const path = match.navigatePath;
    // Prefer the prompt that produced THIS match (carried on the match via
    // `match.prompt`) so clicking a chip from an earlier prompt — or a
    // hydrated session — resolves filters for the correct prompt. Falls back
    // to `lastUserPrompt` for older persisted messages that don't carry it.
    // When `skipFilterApply` is true (loop-initiated pure navigation) the
    // prompt is intentionally left undefined so no filter application occurs.
    // Likewise with `resolvedFilters`: the artifact IS the filter set, so the
    // prompt must never feed a re-resolution (discovery search included).
    const promptForFilters =
      options?.skipFilterApply || options?.resolvedFilters
        ? undefined
        : getDiscoveryApplyPrompt(
            match,
            options?.prompt?.trim() ||
              this.d.lastUserPrompt?.trim() ||
              undefined
          );
    const action = getGlobalAssistantPageMatchAction(match, promptForFilters);
    const entityType = this.inferEntityTypeForPath(path);
    const requestId = crypto.randomUUID();
    const isAlreadyOnTargetPage = globalAssistantPathsMatch(
      this.d.getCurrentRoute(),
      path
    );
    const guard = await this.confirmAssistantRedirect(path);
    // The guard can await the in-app dialog; an unmount meanwhile settles it
    // as "stay" and disposes this instance, and the transcript store outlives
    // the engine — so re-check before writing anything.
    if (this.disposed) return { navigated: false };
    if (!guard.shouldContinue) {
      this.d.appendTranscript({
        id: crypto.randomUUID(),
        role: 'assistant',
        kind: 'text',
        text: 'Navigation canceled. I kept you on this page so your unsaved changes remain.',
      });
      return { navigated: false };
    }

    mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_OPTION, {
      choice: options?.standard ? 'standard_template_page' : 'existing_page',
      trigger: options?.trigger ?? 'click',
      navigate_path: path,
      conversation_id: this.stable.conversationTrackingIdRef.current,
      turn_index: turnIndexOfMessage(
        this.d.getTranscript(),
        options?.answerMessageId
      ),
    });

    if (options?.resolvedFilters) {
      // Deterministic apply: emit the ALREADY-resolved artifact as a resolved
      // `applyRouteFilters` command, then navigate — the same
      // emit-before-navigate sequencing `resolveThenEmitRouteFilters` uses (the
      // command waits in the store until the destination bridge mounts and
      // claims it by targetPath + entityType; see the command-store docblock).
      // Same mechanics as the turn runner's `applyFilters` handle: no
      // `getRouteFilterResponse`, no discovery re-search — re-resolution must
      // never happen on this path. Replace semantics (no `merge`): the click
      // is a fresh landing on the answer's exact filter scope.
      this.setStatusWithTtlClear('Applying filters…');
      this.emitResolvedRouteFilters({
        requestId,
        path,
        response: options.resolvedFilters,
        // Metadata only (tracking + bridge context); NEVER used to resolve.
        prompt: options?.prompt?.trim() || this.d.lastUserPrompt?.trim() || '',
        // Commands are claimed by the destination page bridge. The artifact's
        // held-constant anchor may differ from that destination (for example,
        // Industry filters on Role Compositions), so route ownership must win.
        entityType,
        // The artifact's role ids are taxonomy-relative — the destination
        // page must adopt the taxonomy they were resolved under, or it
        // queries them under the user's preference taxonomy and the backend
        // rejects the request (`role_v3_id = []`). This is a user-initiated
        // "show me this answer's filters" click, so pairing IS the correct
        // page behavior here — same as every other `applyRouteFilters` now
        // (pairing is default-on; see the command-store docblock). Passed
        // explicitly anyway for clarity at this call site.
        pairTaxonomy: true,
      });
      if (!isAlreadyOnTargetPage) {
        this.d.navigate(
          path,
          guard.skipGuard ? { state: { skipGuard: true } } : undefined
        );
      }
      guard.discardChanges?.();
      return { navigated: true };
    }

    if (action.kind === 'apply_discovery_filters_after_navigate') {
      mixpanel.track(
        BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_QUEUED,
        {
          ...(action.discoveryKind === 'talent'
            ? {}
            : { entity_type: action.discoveryKind }),
          global_assistant_discovery_kind: action.discoveryKind,
          global_assistant_route_filter_request_id: requestId,
        }
      );
      this.setStatusWithTtlClear(
        isAlreadyOnTargetPage ? 'Applying filters…' : 'Opening Discovery…'
      );
      // RESOLVE-FIRST (meeting decision 2026-07-08): the discovery search used
      // to run on the DESTINATION after landing (runDiscoveryAiSearchAfterNavigate,
      // deleted with this change) — the user watched an unfiltered surface
      // resolve. Resolve drawer-side instead, then emit the resolved apply and
      // navigate so the destination mounts with its command waiting. Failure:
      // stay put and say so.
      const discoveryResolvedOk = await this.resolveThenEmitRouteFilters({
        path,
        promptForFilters: promptForFilters ?? '',
        entityType,
        requestId,
      });
      if (!discoveryResolvedOk) {
        this.d.appendTranscript({
          id: crypto.randomUUID(),
          role: 'assistant',
          kind: 'error',
          text: `Couldn't resolve that search for ${match.name}, so I stayed on this page — you can retry, or open ${match.name} from the navigation and search there.`,
        });
        return { navigated: false };
      }
      if (!isAlreadyOnTargetPage) {
        this.d.navigate(
          path,
          guard.skipGuard ? { state: { skipGuard: true } } : undefined
        );
      }
      guard.discardChanges?.();
      return { navigated: true };
    }

    if (action.kind === 'resolve_route_filters' && promptForFilters) {
      if (isAlreadyOnTargetPage) {
        this.d.appendTranscript({
          id: crypto.randomUUID(),
          role: 'assistant',
          kind: 'text',
          text: SAME_PAGE_FILTER_APPLY_MESSAGE,
        });
      }
      // RESOLVE-FIRST: await resolution BEFORE navigating so the destination
      // mounts with its resolved apply command already waiting. On failure:
      // stay put and say so (landing unfiltered used to masquerade as success).
      const resolvedOk = await this.resolveThenEmitRouteFilters({
        path,
        promptForFilters,
        entityType,
        requestId,
      });
      // Check failure BEFORE the same-page success return: when already on the
      // target page and resolution FAILS, an early `navigated: true` here would
      // shadow this error path.
      if (!resolvedOk) {
        this.d.appendTranscript({
          id: crypto.randomUUID(),
          role: 'assistant',
          kind: 'error',
          text: `Couldn't resolve the filters for ${match.name}, so I stayed on this page — you can retry, or open ${match.name} unfiltered from the navigation.`,
        });
        return { navigated: false };
      }
      if (isAlreadyOnTargetPage) {
        guard.discardChanges?.();
        return { navigated: true };
      }
    }

    this.d.navigate(
      path,
      guard.skipGuard ? { state: { skipGuard: true } } : undefined
    );
    guard.discardChanges?.();
    return { navigated: true };
  };

  readonly startCustomPageCreate = async (
    entityType: CustomDashAssistantEntityType,
    options?: {
      prompt?: string;
      contextPolicy?: GlobalAssistantContextPolicy;
      /**
       * Draft-only mode: create the empty page draft and navigate to it, but do
       * NOT emit the `startCustomPageStream` build command. Used by the
       * confirm-navigate-resume flow (the chip click navigates to an empty
       * editable draft, then RESUMES the loop turn so the model builds the page
       * itself via `update_current_page` — native reasoning trace + grounded
       * narration). The normal build path leaves this unset.
       */
      skipBuild?: boolean;
      /** The answer whose build link was clicked. */
      answerMessageId?: string;
    }
    // Async because the unsaved-changes guard can open the in-app dialog.
  ): Promise<{ ok: boolean; targetPath?: string }> => {
    if (this.isDisposed('startCustomPageCreate')) return { ok: false };
    // Prefer the prompt attached to the message that triggered the click
    // (so the "Create custom page" button still works after session
    // hydration, where `lastUserPrompt` is not restored from localStorage).
    const text =
      options?.prompt?.trim() || this.d.lastUserPrompt?.trim() || undefined;
    if (!text) {
      setEngineError('Run a prompt first so we know what to build.');
      return { ok: false };
    }
    const guard = await this.confirmAssistantRedirect();
    // See navigateToMatch: the dialog await can outlive the mount.
    if (this.disposed || !guard.shouldContinue) {
      return { ok: false };
    }
    // Snapshot the CLICK-time page BEFORE creating the draft: with no route
    // loaders, react-router's data router completes `navigate()` (including
    // `history.push`) synchronously inside `createPageDraft`, so a live read
    // after it already sees the draft path and would never discard changes.
    const clickPathname = this.d.getCurrentRoute();
    const targetPath = this.d.createPageDraft(entityType, guard.skipGuard);
    if (!targetPath) {
      return { ok: false };
    }
    const targetPathname = normalizeAndNavigateToPath(
      targetPath,
      this.d.navigate,
      guard.skipGuard
    );
    const isSamePath = globalAssistantPathsMatch(clickPathname, targetPathname);
    if (!isSamePath) {
      guard.discardChanges?.();
    }
    const contextPolicy = resolveFollowOnContextPolicy(options?.contextPolicy);
    // Fire BEFORE the draft-only early-return: the skipBuild path is a real
    // build-accept click (the inline "build" offer link), so it counts too.
    mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_OPTION, {
      choice: 'custom_page',
      build_mode: options?.skipBuild ? 'draft_resume' : 'direct_build',
      trigger: 'click',
      global_assistant_context_policy: contextPolicy,
      conversation_id: this.stable.conversationTrackingIdRef.current,
      turn_index: turnIndexOfMessage(
        this.d.getTranscript(),
        options?.answerMessageId
      ),
    });
    // Draft-only: created the empty draft + navigated. Skip the build emit and
    // hand the target path back so the caller can arm a pending resume keyed on
    // it (the resumed turn builds the page on this draft).
    if (options?.skipBuild) {
      return { ok: true, targetPath: targetPathname };
    }
    const fullConversationContext = buildBoundedConversationContext({
      transcript: this.d.getTranscript(),
      summary: this.d.conversationSummary,
    });
    const conversationPayload = toConversationPayload(
      applyConversationPolicy({
        policy: contextPolicy,
        fullConversation: fullConversationContext,
      })
    );
    this.emit({
      kind: 'startCustomPageStream',
      requestId: crypto.randomUUID(),
      prompt: text,
      entityType,
      mode: 'create',
      targetPath: targetPathname,
      ...(conversationPayload ? { conversation: conversationPayload } : {}),
    });
    this.d.setGenerationStatus('Opening custom page…');
    return { ok: true, targetPath: targetPathname };
  };

  /**
   * The inline `[phrase](offer:geo_tax)` narration link: switch the GLOBAL
   * geography taxonomy preference the user was NOT on, then re-scope the page
   * under it. `nextTaxonomyId` arrives from the turn's `filter_response`
   * artifact (stamped in `offerTargets.geo_tax`), never from the model's
   * visible text.
   *
   * The assistant NEVER switches the taxonomy on its own — the preference is
   * account-wide and re-keys every page — so this only ever runs from an
   * explicit click, which IS the consent.
   */
  readonly switchGeographyTaxonomy = async (
    nextTaxonomyId: number,
    /**
     * The ask that earned this offer, carried on the narration message. The
     * switch re-resolves filters, so an offer clicked after a newer, unrelated
     * ask must still re-resolve ITS OWN ask — `lastUserPrompt` would apply the
     * newer one under the freshly switched taxonomy. Falls back to
     * `lastUserPrompt` only for narrations persisted before this was carried.
     */
    promptForOffer?: string,
    /** The offer came from a chart; see `reaskOnly` on the switch. */
    reaskOnly?: boolean
  ): Promise<void> => {
    if (this.isDisposed('switchGeographyTaxonomy')) return;
    if (this.geoTaxSwitchInFlight) {
      return;
    }
    this.geoTaxSwitchInFlight = true;
    setEngineError(null);
    const currentPath = this.d.getCurrentRoute();
    const switchEntityType = this.inferEntityTypeForPath(currentPath);
    try {
      await runGeographyTaxonomySwitch({
        nextTaxonomyId,
        // The trigger stays clickable after the switch; once the account is
        // already on the offered view a second click is a no-op, not another
        // preference write + re-ask (two clicks, two re-asks, 2026-09-03).
        currentTaxonomyId: this.d.taxonomyByEntity(switchEntityType).geoTax,
        path: currentPath,
        entityType: switchEntityType,
        promptForFilters: promptForOffer?.trim() || this.d.lastUserPrompt,
        canApplyFiltersOnCurrentPage:
          isGlobalAssistantFilterApplyCapablePath(currentPath) ||
          this.stable.customDashAssistContextRef.current.isInCustomDashEditMode,
        reaskOnly,
        resolveThenEmitRouteFilters: this.resolveThenEmitRouteFilters,
        // Off a filterable page (homepage data answer), re-ask the question
        // under the new view as a normal turn: the fresh answer re-resolves
        // the geography and carries the page link. `startAssistantTurn`
        // guards `isTurnRunning` itself; the click never happens mid-turn.
        reask: (prompt) => {
          void this.startAssistantTurn(prompt);
        },
        notify: ({ text, kind }) => {
          this.d.appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind,
            text,
          });
        },
      });
    } finally {
      this.geoTaxSwitchInFlight = false;
    }
  };

  // --- Report / build cluster --------------------------------------------

  /**
   * The prefill digest is fully structured, so the confirmation line is built
   * deterministically — no LLM, no network call. APPENDED as a new message,
   * never written into an existing bubble: the latest text is often a STAMPED
   * offer narration (inline `[Report](offer:report)` trigger), and rewriting
   * it deletes the marker — which un-suppresses the report cards and destroys
   * the clickable explanation.
   */
  private narrateReportPrefill(
    digest: Parameters<typeof formatReportPrefillNarration>[0]
  ): void {
    this.d.appendTranscript({
      id: crypto.randomUUID(),
      role: 'assistant',
      kind: 'text',
      text: formatReportPrefillNarration(digest),
    });
  }

  /**
   * Report-offer card click: access gate → launch lock → track → redirect
   * guard → `launchReportFromOffer`. Every read is CLICK-time (live route,
   * live transcript, this render's flags/config), which is what a click
   * handler wants — the `taxonomyByEntityRef` mirror this used to carry bought
   * nothing over the plain `taxonomyByEntity` dep.
   */
  readonly handleReportOfferClick = async (
    offer: ReportOfferCardFields,
    prompt: string | undefined,
    messageId: string
  ): Promise<void> => {
    if (this.isDisposed('handleReportOfferClick')) return;
    const promotedTemplate = getPromotedTemplateForReportOfferClick(
      offer.reportType,
      this.d.allFlags,
      // Same Unleash toggles as `allFlags` (one client, one `update` event), so
      // the CustomReports read is derived here rather than carried as a second
      // dep the type would let disagree with the map.
      this.d.allFlags[FeatureFlag.CustomReports]
    );
    const access = getGlobalAssistantReportAccess(
      offer.reportType,
      offer.reportName,
      this.d.reportBuilderConfig,
      promotedTemplate !== null
    );
    if (!access.allowed) {
      this.d.appendTranscript({
        id: crypto.randomUUID(),
        role: 'assistant',
        kind: 'error',
        text: access.message,
      });
      return;
    }

    setReportOfferLaunchingKey(
      buildReportOfferLaunchKey(messageId, offer.reportType)
    );
    try {
      mixpanel.track(
        BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_REPORT_CLICKED,
        {
          global_assistant_report_type: offer.reportType,
          conversation_id: this.stable.conversationTrackingIdRef.current,
          turn_index: countUserTurns(this.d.getTranscript()),
        }
      );

      const entityPath = getReportBuilderPath(ReportBuilderStep.ENTITY);
      const guard = await this.confirmAssistantRedirect(entityPath);
      // See navigateToMatch: the dialog await can outlive the mount.
      if (this.disposed) return;
      if (!guard.shouldContinue) {
        this.d.appendTranscript({
          id: crypto.randomUUID(),
          role: 'assistant',
          kind: 'text',
          text: 'Navigation canceled. I kept you on this page so your unsaved changes remain.',
        });
        return;
      }

      const promptForParams =
        prompt?.trim() || this.d.lastUserPrompt?.trim() || '';

      await launchReportFromOffer({
        offer: {
          reportType: offer.reportType,
          reportName: offer.reportName,
          confidence: offer.confidence,
          sampleLink: offer.sampleLink,
          description: offer.description ?? '',
        } satisfies GlobalAssistantReportOffer,
        promptForParams,
        roleTaxonomy: this.d.taxonomyByEntity('role'),
        promotedTemplate,
        reportEntityMap: this.d.reportBuilderConfig,
        pathname: this.d.getCurrentRoute(),
        navigate: this.d.navigate,
        skipGuard: guard.skipGuard,
        onPrefillError: (text) => {
          this.d.appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'error',
            text,
          });
        },
        onPrefillWarning: (text) => {
          this.d.toast({
            title: 'Some report filters need manual selection',
            description: text,
            status: 'warning',
            duration: 7000,
            isClosable: true,
            position: 'top-right',
          });
        },
        onPrefillSuggestions: (suggestions) => {
          this.narrateReportPrefill(
            buildReportPrefillDigest({
              reportName: offer.reportName,
              suggestions,
            })
          );
        },
      });
      if (guard.skipGuard) {
        guard.discardChanges?.();
      }
    } finally {
      setReportOfferLaunchingKey(null);
    }
  };

  /**
   * LAZY click-time generation for build chips WITHOUT a pre-generated layout
   * (offer_page_or_custom's secondary chip). Runs the page-gen stream
   * headlessly while the user is STILL on the current page, streaming its
   * reasoning into a self-contained trace (live thinking → "Worked for Xs")
   * exactly like a turn's create step. The wiring then stashes the layout and
   * navigates to a COMPLETE draft (resolve-first, meeting 2026-07-08). Null →
   * the wiring falls back to the legacy resume flow.
   */
  readonly generateForBuildClick = async (
    prompt: string,
    entityType: CustomDashAssistantEntityType
  ): Promise<{
    page: HeadlessGeneratedPage | null;
    buildTrace?: { traceId: string; trace: ReasoningTrace };
  }> => {
    if (this.isDisposed('generateForBuildClick')) return { page: null };
    // The click starts no TURN, so nothing else would ever abort this build:
    // park its controller in the in-flight-stream slot that `cancelTurn` and
    // `closeDrawer` already abort. Without a live signal the build runs to
    // completion after the user closed the drawer and the wiring then yanks
    // them into a draft they walked away from, and
    // `generateDashboardHeadless`'s abort rethrow is unreachable here.
    const { streamAbortRef } = this.stable;
    streamAbortRef.current?.abort();
    const buildController = new AbortController();
    streamAbortRef.current = buildController;
    try {
      return await runHeadlessBuildWithTrace({
        prompt,
        entityType,
        taxonomy: this.headlessBuildTaxonomy(entityType),
        signal: buildController.signal,
        conversation: toConversationPayload(
          buildBoundedConversationContext({
            // Live store read (the dep IS the store read): the click can land
            // long after the render that installed this dep set.
            transcript: this.d.getTranscript(),
            summary: this.d.conversationSummary,
          })
        ),
        newId: () => crypto.randomUUID(),
        nowMs: () => Date.now(),
        appendTranscript: this.d.appendTranscript,
        updateReasoningTrace: this.d.updateReasoningTrace,
        updateAssistantMessageText: this.d.updateAssistantMessageText,
        setGenerationStatus: this.d.setGenerationStatus,
      });
    } finally {
      if (streamAbortRef.current === buildController) {
        streamAbortRef.current = null;
      }
    }
  };

  /**
   * Landing narration for resolve-first builds: builds the consume-once
   * `onSeeded` callback the build click stashes with its generated page —
   * "Reading the page data" while the seeded draft's widgets settle, then a
   * silent narrate turn grounded in their digests (seeded-build-narration.ts).
   * The collector completes up to 30s after the click, long past the render
   * that installed this dep set — which is why the turn starter is this
   * instance's own bound method and the pathname is the live route accessor.
   */
  readonly beginSeededBuildNarration = (params: {
    prompt: string;
    targetPathname: string;
    filters?: TalentDiscoveryAiFilterSearchResponseV2;
    resumeTrace?: { traceId: string; trace: ReasoningTrace };
  }): ((seededItems: PositionedItem[]) => void) => {
    if (this.isDisposed('beginSeededBuildNarration')) return () => undefined;
    return beginSeededBuildNarration(params, {
      newId: () => crypto.randomUUID(),
      nowMs: () => Date.now(),
      appendTranscript: this.d.appendTranscript,
      updateReasoningTrace: this.d.updateReasoningTrace,
      startAssistantTurn: this.startAssistantTurn,
      getCurrentPathname: () => this.d.getCurrentRoute(),
      collectorRef: this.seededBuildCollectorSlot,
    });
  };

  // --- One-shot continuation arming --------------------------------------
  // Thin published wrappers over the store action (see `armContinuation` for
  // the last-click-wins / consume-once semantics). They carry the disposed
  // guard for the same reason every other published method does: an offer
  // click handler memoized in the transcript pane can outlive this mount, and
  // arming a continuation on a dead engine would leave it for whichever engine
  // mounts next.

  readonly armNavResume = (next: {
    continuationPrompt: string;
    pageName: string;
    expectedPath: string;
  }): void => {
    if (this.isDisposed('armNavResume')) return;
    armContinuation({ kind: 'nav_resume', ...next });
  };

  readonly armCreatePageResume = (next: {
    seed: string;
    expectedPath: string;
  }): void => {
    if (this.isDisposed('armCreatePageResume')) return;
    armContinuation({ kind: 'create_page_resume', ...next });
  };

  readonly armBuildHere = (): void => {
    if (this.isDisposed('armBuildHere')) return;
    armContinuation({ kind: 'build_here' });
  };

  // --- Command-channel drains --------------------------------------------
  // Called from the component's channel effects, which keep the REACT
  // scheduling (subscribe to the channel, re-run on its identity / on the
  // turn-running flip) while the drain logic and the seq watermarks live
  // here (the failure watermark is module-level and conversation-keyed — see
  // `filterApplyFailureWatermark`; the adjustment watermark is per instance). Deliberately NOT store subscriptions: these must fire at COMMIT
  // time, not publish time — see `consumeFilterApplyAdjustments`.
  //
  // DO NOT add an `isDisposed` guard to these two "for consistency" with the
  // published methods. Effects register in source order — warm-up(1), failure
  // drain(2), adjustment drain(3), continuation(4), publish/mount(5) — and
  // React runs every cleanup before every setup, so on a remount that reuses
  // this instance (StrictMode's simulated remount, which RTL enables) the two
  // drains run at 2 and 3 while `disposed` is still true from the previous
  // teardown: `onMount` does not clear it until 5. A guard here would silently
  // swallow the first drain of every remount. Unguarded is also SAFE: the seq
  // watermark makes a drain idempotent, so the worst a post-dispose call can
  // do is append a message the user was owed anyway.

  /**
   * apply_filters is optimistic (DP4): the turn ends when the command is
   * ENQUEUED, so an apply the chat already promised can still die two ways —
   * the command ages out unconsumed (TTL) or a bridge consumes it and the
   * apply throws. Surface each failure event as an in-chat message.
   *
   * `conversationId` keys the MODULE-level watermark (see
   * `filterApplyFailureWatermark`): a new id re-seeds at the channel head and
   * surfaces nothing; the same id on a fresh engine instance (remount after a
   * hidden-path detour) continues from where the previous instance left off,
   * so a failure published while no engine was mounted still reaches the
   * chat. The component passes its subscribed tracking id, so the re-seed
   * lands at the id change rather than at the next publish.
   */
  readonly consumeFilterApplyFailures = (conversationId: string): void => {
    const channel =
      useGlobalAiCommandStore.getState().channels.filterApplyFailure;
    if (filterApplyFailureWatermark.conversationId !== conversationId) {
      filterApplyFailureWatermark.conversationId = conversationId;
      filterApplyFailureWatermark.seq = channel.seq;
      return;
    }
    for (const { seq, event } of channelEventsSince(
      channel,
      filterApplyFailureWatermark.seq
    )) {
      filterApplyFailureWatermark.seq = seq;
      const where = event.paths.join(', ');
      const why =
        event.reason === 'error'
          ? 'the page hit an error while applying them'
          : "the page didn't confirm in time";
      this.d.appendTranscript({
        id: crypto.randomUUID(),
        role: 'assistant',
        kind: 'text',
        text: `I couldn't finish applying those filters to ${where} — ${why}. Please try again.`,
      });
    }
  };

  /**
   * Surface the filter cap correction — see the `filterApplyAdjustment`
   * channel docblock for why the chat needs it at all.
   *
   * The apply lands mid-turn, while the model is still streaming the message
   * this corrects. Appending then would put the correction ABOVE the claim it
   * corrects, so everything is HELD until the turn finishes: this bails with
   * the watermark unadvanced while a turn runs, and the caller's effect
   * re-runs when the turn-running flag flips, with the held events still
   * pending.
   */
  readonly consumeFilterApplyAdjustments = (): void => {
    const state = useGlobalAiEngineStore.getState();
    if (selectIsTurnRunning(state)) {
      return;
    }
    const turnStartedAt = state.turn?.startedAt ?? 0;
    const channel =
      useGlobalAiCommandStore.getState().channels.filterApplyAdjustment;
    for (const { seq, at, event } of channelEventsSince(
      channel,
      this.seenAdjustmentSeq
    )) {
      this.seenAdjustmentSeq = seq;
      // Both referents must still be on screen — the claim being corrected and
      // the page it describes — or the correction misattributes to whatever is
      // there now. "The claim" is anchored to the turn record the store keeps
      // past `endTurn` (its startedAt survives the turn ending — that is why
      // `endTurn` retires the record instead of nulling it): an adjustment
      // older than the most recent turn's start belongs to a question the user
      // has already moved past. Page identity goes through
      // `globalAssistantPathsMatch`, the normalizer the store and bridge pair
      // commands with: raw equality would reject an apply the bridge accepted
      // (trailing slashes, screener sub-segments) and drop every correction.
      // The page read is LIVE, and the caller invokes this from an effect —
      // i.e. post-commit, so at least as fresh as the render that scheduled it.
      const superseded =
        at < turnStartedAt ||
        !globalAssistantPathsMatch(this.d.getCurrentRoute(), event.path);
      if (superseded) {
        continue;
      }
      const dropped = event.droppedNames;
      this.d.appendTranscript({
        id: crypto.randomUUID(),
        role: 'assistant',
        kind: 'text',
        text: `This page couldn't show all of them, so ${formatDroppedNames(dropped)} ${dropped.length === 1 ? 'was' : 'were'} left off.`,
      });
    }
  };
}
